import React from 'react';
import { DeckIcon } from '~/design-system/elements/DeckIcon/DeckIcon';
import { cn } from '~/lib/utils';
import themes from './HeaderTheme.module.css';
import styles from './IconSlot.module.css';
import type { HeaderHover, HeaderTheme } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

export interface IconSlotProps {
  /** Registry name of the resting glyph, e.g. `share-2`. */
  icon: string;
  /** Registry name of the `-fill` twin shown while `active`. Omit for controls with no active state. */
  activeIcon?: string;
  active?: boolean;
  /**
   * `aria-pressed`, for a toggle whose ON state is NOT the `-fill` twin.
   *
   * Every control in the header proper says "on" by swapping its glyph, so `aria-pressed` can be
   * derived from `activeIcon` + `active` and no caller has to think about it. The Preview bar's lit
   * controls are the exception the derivation cannot cover: they paint a chip and keep the resting
   * glyph, so they have an `active` twin nowhere in the registry and would otherwise announce
   * identically to the un-lit control beside them — the state would be colour alone. Overrides the
   * derivation when given; leave it off everywhere else.
   */
  pressed?: boolean;
  /** Glyph box in px. 18 everywhere except `agent`, which is drawn on a 20-unit viewBox. */
  glyphSize?: number;
  /** Accessible name — these are icon-only controls, so it is required. */
  label: string;
  /**
   * For a slot that opens a popup: what it opens, and whether it is open right now.
   *
   * A menu trigger is not a toggle. Without these a screen reader announces "toggle button, not
   * pressed" and says nothing about the menu that appeared, which is a state change on the button
   * standing in for the one thing that actually happened. Supplying `ariaExpanded` also suppresses
   * `aria-pressed` below, so the two never announce two different states of one control.
   */
  ariaHasPopup?: 'menu' | 'listbox' | 'dialog';
  ariaExpanded?: boolean;
  title?: string;
  disabled?: boolean;
  onClick?: () => void;
  theme?: HeaderTheme;
  hover?: HeaderHover;
  /** Stories and visual tests only: paints the hover state without a real pointer. */
  forceHover?: boolean;
  className?: string;
}

/**
 * The header's only control shape: a 30x30 round slot holding an 18px glyph. **Active paints no
 * background** — it is the `-fill` twin of the same glyph at the same tone
 * (`--color-text-white-muted`). Hover is the one state that paints, because it cannot persist:
 * the chip belongs to the cursor and leaves with it.
 */
export const IconSlot = React.forwardRef<HTMLButtonElement, IconSlotProps>(function IconSlot(
  {
    icon,
    activeIcon,
    active = false,
    pressed,
    glyphSize = 18,
    label,
    ariaHasPopup,
    ariaExpanded,
    title,
    disabled = false,
    onClick,
    theme: themeProp,
    hover = 'shape',
    forceHover = false,
    className,
  },
  ref,
) {
  const theme = useV15Theme(themeProp);
  const name = active && activeIcon ? activeIcon : icon;

  return (
    <button
      ref={ref}
      type="button"
      className={cn(
        themes[theme],
        styles.slot,
        hover === 'tone' && styles.tone,
        hover === 'shape' && styles.shape,
        forceHover && styles.forceHover,
        className,
      )}
      aria-label={label}
      aria-haspopup={ariaHasPopup}
      aria-expanded={ariaExpanded}
      aria-pressed={ariaExpanded === undefined ? (pressed ?? (activeIcon ? active : undefined)) : undefined}
      title={title ?? label}
      disabled={disabled}
      onClick={onClick}
    >
      <DeckIcon name={name} size={glyphSize} />
    </button>
  );
});
